import { useEffect, useRef, useState } from 'react';
import { useObserver } from 'mobx-react-lite';

import useDispensary from 'dispensary/hooks/use-dispensary';
import useCart from 'hooks/use-cart';
import useUser from 'hooks/use-user';
import { loadCart } from 'hooks/use-cart-controller';
import { getStoredCartKey, normalizeAndUpdateStoredCart } from 'shared/actions/cart';

import { imageToUse } from 'shared/helpers/products';
import { getPersistedValue } from 'shared/utils/persisted-values';
import { LEGACY_AWS_SOURCE, IMGIX_SOURCE, LOCAL_STORAGE_STORED_CART_KEY } from 'shared/constants';

import useUI from 'hooks/use-ui';
import { useGetChainLocations } from './use-get-chain-locations';
import { getIsUserInLegalMarket, getThemeColorForDispensary } from './use-mobile-native-bridge.utils';
import { UserLocation } from './use-mobile-native-bridge.types';

export function callNativeMethod(method: string, params?: any): void {
  // eslint-disable-next-line dot-notation
  if (window['flutter_inappwebview']) {
    // eslint-disable-next-line dot-notation
    window['flutter_inappwebview'].callHandler(method, params);
  }
}

function registerNativeHandler(name: string, callback: () => Promise<any>): void {
  window[`flutter_handler_${name}`] = callback;
}

function unregisterNativeHandler(name: string): void {
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete window[`flutter_handler_${name}`];
}

function useNativeBridgeHandler(method: string, loading: boolean, value: any): void {
  const promiseRef = useRef<any>();
  const [lastLoading, setLastLoading] = useState<boolean>(loading);

  function newPromiseObject(): { promise: Promise<any>; resolve: (val: any) => void; reject: (err: any) => void } {
    const result: any = {};
    result.promise = new Promise((res, rej) => {
      result.resolve = res;
      result.reject = rej;
    });
    return result;
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!promiseRef.current) {
    promiseRef.current = newPromiseObject();
  }
  useEffect(() => {
    // if we weren't loading before but now we are
    if (!lastLoading && loading) {
      promiseRef.current = newPromiseObject();
    } else if (!lastLoading && !loading) {
      // not loading, and loading didn't change, so value did
      promiseRef.current = newPromiseObject();
      promiseRef.current.resolve(value);
    } else if (lastLoading && !loading) {
      // we were loading but now we're not loading anymore
      promiseRef.current.resolve(value);
    }
    // loading changes, store new state last
    if (lastLoading !== loading) {
      setLastLoading(loading);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, value]); // FIXME: ENG-32714 fix hooks dependency

  useEffect(() => {
    registerNativeHandler(method, async () => promiseRef.current.promise);

    return () => unregisterNativeHandler(method);
  }, [method]);
}

export function useMobileNativeBridge(): void {
  const [location, setLocation] = useState<UserLocation | null>(null);

  const { dispensary, loading: dispensaryLoading } = useDispensary();
  const { chainLocations, chainLocationsLoading } = useGetChainLocations({
    chain: dispensary?.chain,
    isUserInLegalMarket: location?.isUserInLegalMarket,
    coordinates: { latitude: location?.latitude, longitude: location?.longitude },
  });
  const Cart = useCart();
  const User = useUser();
  const { isProductDetailPage, isCartPage, isCheckoutPage } = useUI();
  const loadingCart = useRef<boolean>(false);

  function setUserLocation(latitude: number, longitude: number, state: string): void {
    setLocation({ isUserInLegalMarket: getIsUserInLegalMarket(state), latitude, longitude, state });
  }

  function reloadCart(): void {
    loadingCart.current = true;
    const storedCarts = normalizeAndUpdateStoredCart(getPersistedValue(LOCAL_STORAGE_STORED_CART_KEY, {}));
    const currentStoredCartKey = getStoredCartKey(dispensary?.id);
    const currentStoredCart = storedCarts[currentStoredCartKey];
    Cart.clearOrder();

    if (currentStoredCart) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      loadCart({ storedDispensaryCart: currentStoredCart, Cart });
    }

    loadingCart.current = false;
  }

  const { mostRecentCartItem, subtotal, mostRecentProduct, itemCount, cartLoading } = useObserver(() => ({
    mostRecentCartItem: Cart.mostRecentCartItem,
    subtotal: Cart.subtotal,
    itemCount: Cart.itemCount,
    mostRecentProduct: Cart.mostRecentCartItem?.product,
    cartLoading: !Cart.isReady,
  }));

  const {
    mostRecentProductBrandName,
    mostRecentProductPrice,
    mostRecentProductName,
    mostRecentProductQuantity,
    mostRecentProductImage,
  } = useObserver(() => ({
    mostRecentProductBrandName: mostRecentProduct?.brand?.name,
    mostRecentProductPrice: mostRecentCartItem?.price,
    mostRecentProductName: mostRecentProduct?.name,
    mostRecentProductQuantity: mostRecentCartItem?.quantity,
    mostRecentProductImage: mostRecentProduct
      ? imageToUse(mostRecentProduct)?.replace(LEGACY_AWS_SOURCE, IMGIX_SOURCE)
      : null,
  }));

  const themeColorForDispensary: string | null = dispensary
    ? getThemeColorForDispensary({ colorKey: dispensary.colorSettings?.linkColor })
    : null;

  const dispensaryInfo = useObserver(() => ({
    themeColor: themeColorForDispensary,
    name: dispensary?.name,
    chain: !!dispensary?.chain,
  }));

  const { isLoggedIn, userLoading } = useObserver(() => ({ isLoggedIn: User.isLoggedIn, userLoading: User.loading }));

  useNativeBridgeHandler(`isLoggedIn`, userLoading, { isLoggedIn });
  useNativeBridgeHandler(`getDispensary`, dispensaryLoading, dispensary);
  useNativeBridgeHandler(`getDispensaryInfo`, dispensaryLoading, dispensaryInfo);
  useNativeBridgeHandler(`getCartQuantity`, cartLoading, { totalCartQuantity: parseInt(Cart.itemCount, 10) });
  useNativeBridgeHandler(`setUserLocation`, false, setUserLocation);
  useNativeBridgeHandler(`getIsUserInLegalMarket`, !location, { isUserInLegalMarket: location?.isUserInLegalMarket });
  useNativeBridgeHandler(`getChainLocations`, chainLocationsLoading, { chainLocations });
  useNativeBridgeHandler(`reloadCart`, cartLoading, reloadCart);
  useNativeBridgeHandler(`clearCart`, cartLoading, Cart.clearOrder);

  useEffect(() => {
    // something was added to cart, or the most recent thingy was dismissed
    if (mostRecentCartItem && !isCartPage && !isCheckoutPage) {
      // something was added to cart
      if (isProductDetailPage) {
        callNativeMethod(`onAddToCart`, {
          quantityAdded: mostRecentProductQuantity,
          totalCartQuantity: itemCount,
        });
      } else {
        const cartTotalPrice: string = subtotal.toFixed(2).toString();
        const price: string = mostRecentProductPrice.toFixed(2).toString();
        callNativeMethod(`onQuickAddToCart`, {
          cartTotalPrice: `$${cartTotalPrice}`,
          company: mostRecentProductBrandName,
          price: `$${price}`,
          product: mostRecentProductName,
          quantity: mostRecentProductQuantity,
          image: mostRecentProductImage,
          totalCartQuantity: itemCount,
        });
      }
      Cart.clearMostRecentProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostRecentCartItem]); // FIXME: ENG-32714 fix hooks dependency

  useEffect(() => {
    if (!cartLoading && !loadingCart.current) {
      callNativeMethod(`onUpdateCart`, {
        totalCartQuantity: itemCount,
      });
    }
  }, [itemCount, cartLoading]);

  useEffect(() => {
    if (userLoading === false) {
      callNativeMethod(`onAuthChange`, {
        isLoggedIn,
      });
    }
  }, [isLoggedIn, userLoading]);
}
